import { Loader2, CircleCheck, AlertCircle, ExternalLink } from 'lucide-react'
import { InstagramIcon } from '@/components/ui/icons/InstagramIcon'
import { LinkedInIcon } from '@/components/ui/icons/LinkedInIcon'
import { TikTokIcon } from '@/components/ui/icons/TikTokIcon'
import { YoutubeIcon } from '@/components/ui/icons/YoutubeIcon'
import { FacebookIcon } from '@/components/ui/icons/FacebookIcon'
import { type SelectedAccount } from '../workspace-header/workspace-header-social-accounts'
import { Card } from '@/components/ui/shadcn/card'
import { Button } from '@/components/ui/shadcn/button'
import {
  type PublishablePlatformType,
  publishablePlatformTypes,
} from '@backend/lib/db/schema/platform'

export type PlatformStatus = 'idle' | 'loading' | 'success' | 'error'

export type PlatformStatusState = Record<PublishablePlatformType, PlatformStatus>

/** All-idle status map — useful as the initial value. */
// eslint-disable-next-line react-refresh/only-export-components
export const idlePlatformStatus = (): PlatformStatusState =>
  Object.fromEntries(publishablePlatformTypes.map((p) => [p, 'idle'])) as PlatformStatusState

/** Per-platform display info shared across status rows. */
const PLATFORM_DISPLAY_INFO: Record<
  PublishablePlatformType,
  { icon: React.ReactNode; name: string; color: string }
> = {
  instagram: {
    icon: <InstagramIcon width="20" height="20" />,
    name: 'Instagram',
    color: 'text-pink-600',
  },
  linkedin: {
    icon: <LinkedInIcon width="20" height="20" />,
    name: 'LinkedIn',
    color: 'text-blue-600',
  },
  tiktok: {
    icon: <TikTokIcon width="20" height="20" />,
    name: 'TikTok',
    color: 'text-black dark:text-white',
  },
  youtube: {
    icon: <YoutubeIcon width="20" height="20" />,
    name: 'YouTube',
    color: 'text-red-600',
  },
  facebook: {
    icon: <FacebookIcon width="20" height="20" />,
    name: 'Facebook',
    color: 'text-[#1877F2]',
  },
}

interface Props {
  selectedAccounts: SelectedAccount[]
  platformStatus: PlatformStatusState
  isScheduling: boolean
  isComplete?: boolean
  postLinks?: Record<string, { link?: string; scheduledPostId?: number; isScheduled: boolean }>
  publishAsDraft?: boolean
  hasVideo?: boolean
  tiktokProcessing?: boolean
  onViewPost?: (
    platform: PublishablePlatformType,
    link: string | undefined,
    scheduledPostId: number | undefined,
    isScheduled: boolean,
  ) => void
}

export const WorkspacePreviewStatus = ({
  selectedAccounts,
  platformStatus,
  isScheduling,
  isComplete = false,
  postLinks = {},
  publishAsDraft = false,
  hasVideo = false,
  tiktokProcessing = false,
  onViewPost,
}: Props) => {
  const platformsToShow = selectedAccounts
    .map((account) => account.platform)
    .filter((platform, index, arr) => arr.indexOf(platform) === index)

  // Calculate progress
  const totalPlatforms = platformsToShow.length
  const hasLinkedIn = platformsToShow.includes('linkedin')
  const hasOtherPlatforms = platformsToShow.some((p) => p !== 'linkedin')
  const completedCount = platformsToShow.filter((platform) => {
    const status = platformStatus[platform]
    return status === 'success' || status === 'error'
  }).length
  const progressPercentage = totalPlatforms > 0 ? (completedCount / totalPlatforms) * 100 : 0

  // Determine progress text based on publishAsDraft and platform selection
  const getProgressText = () => {
    if (isScheduling) {
      return 'Scheduling to'
    }
    if (publishAsDraft && hasLinkedIn) {
      // If only LinkedIn is selected, say "Posting draft to"
      // If LinkedIn + other platforms, say "Publishing to" (since other platforms are published)
      return hasOtherPlatforms ? 'Publishing to' : 'Posting draft to'
    }
    return 'Publishing to'
  }

  return (
    <div className="mx-auto flex w-full max-w-[450px] flex-col gap-3 py-12">
      {/* Progress Bar */}
      <div className="mb-4 flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span>
            {getProgressText()} {totalPlatforms} platform
            {totalPlatforms !== 1 ? 's' : ''}
          </span>
          <span>
            {completedCount}/{totalPlatforms} completed
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-green-500 transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      {platformsToShow.map((platform) => {
        const status = platformStatus[platform]
        const isLinkedIn = platform === 'linkedin'
        const isSchedulingMode = isScheduling

        const postLink = postLinks[platform]
        // Don't show view button for LinkedIn drafts since the post doesn't exist yet
        const hasViewButton =
          status === 'success' &&
          postLink &&
          (postLink.link || postLink.scheduledPostId) &&
          !(isLinkedIn && publishAsDraft && !postLink.isScheduled)

        const platformInfo = PLATFORM_DISPLAY_INFO[platform]

        return (
          <Card key={platform} className="flex items-center">
            <div className={`flex items-center gap-2 ${platformInfo.color}`}>
              {platformInfo.icon}
              <span className="font-medium">{platformInfo.name}</span>
            </div>

            <div className="flex flex-1 items-center justify-end gap-3">
              {status === 'loading' && (
                <div className="flex items-center gap-2 text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">
                    {isSchedulingMode
                      ? 'Scheduling...'
                      : isLinkedIn && publishAsDraft
                        ? 'Posting draft...'
                        : 'Publishing...'}
                  </span>
                </div>
              )}
              {status === 'success' &&
                platform === 'tiktok' &&
                tiktokProcessing &&
                !isSchedulingMode && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                )}
              {status === 'success' &&
                !(platform === 'tiktok' && tiktokProcessing && !isSchedulingMode) && (
                  <>
                    <div className="flex items-center gap-2 text-green-600">
                      <CircleCheck className="h-4 w-4" />
                      <span className="text-sm">
                        {isSchedulingMode
                          ? 'Scheduled!'
                          : isLinkedIn && publishAsDraft
                            ? 'Posted as draft!'
                            : 'Published!'}
                      </span>
                    </div>
                    {hasViewButton && onViewPost && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          onViewPost(
                            platform,
                            postLink.link,
                            postLink.scheduledPostId,
                            postLink.isScheduled,
                          )
                        }
                        className="h-7 text-xs">
                        View Post
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </>
                )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">Failed</span>
                </div>
              )}
              {status === 'idle' && (
                <div className="flex items-center gap-2 text-gray-500">
                  <span className="text-sm">Waiting...</span>
                </div>
              )}
            </div>
          </Card>
        )
      })}
      {isComplete && (
        <div className="mt-4 flex flex-col gap-2 text-center text-sm">
          <p>
            {publishAsDraft && hasLinkedIn
              ? hasOtherPlatforms
                ? '✨ Your posts have been published and your draft has been posted!'
                : '✨ Your draft has been posted!'
              : '✨ Your posts are ready to view!'}
          </p>
          {hasVideo && (
            <p className="text-muted-foreground text-xs">
              Videos may take a few minutes to finish processing on each platform.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
